/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { CalendarOutlined, FlagOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { COPY_IDEA, Idea, SuggestionResult } from '@frontend/data-access';
import { ScoreTag, SuggestionCard } from '@frontend/feature-idea-suggestion';
import {
  formatChallengeDateWithTimezone
} from '@frontend/shared/util';
import { Button, Image, Typography } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React, { useState } from 'react';

const { Text } = Typography;

export interface ChallengeCardSuggestionProps {
  result: SuggestionResult;
  className?: string;
}

export function ChallengeCardSuggestion({
  result,
  className,
}: ChallengeCardSuggestionProps) {
  const { t } = useTranslation('common');
  const router = useRouter();
  const { id } = router.query;
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const detailUrl = `/intrapreneurship/challenge/${result.internalChallenge?.id}`;

  const details = [
    {
      icon: <CalendarOutlined />,
      text: result.internalChallenge?.registrationEndedAt ? (
        t('registration_ended')
      ) : (
        <>
          {formatChallengeDateWithTimezone(
            result.internalChallenge?.registrationEndAt || '',
            result.internalChallenge?.timezone || '',
            'D MMM YYYY, HH:mm'
          )}
        </>
      ),
    },
    {
      icon: <FlagOutlined />,
      text: result.internalChallenge?.regions
        ?.map((region) => region.name)
        ?.join(', '),
    },
  ];

  const checkApplyEnabled = () => {
    return (
      result.internalChallenge?.state === 'ACTIVE' &&
      !result.internalChallenge?.registrationEndedAt
    );
  };

  const handleCompleted = ({ ideaCopy }: { ideaCopy: Idea }) => {
    router.push({
      pathname: '/idea/create/[id]/scratch',
      query: { id: ideaCopy?.id },
    });
  };

  const [reuseIdea, { loading: submitLoading }] = useMutation(COPY_IDEA, {
    onCompleted: handleCompleted,
  });

  const handleApply = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    reuseIdea({
      variables: { challengeId: result.internalChallenge?.id, ideaId: id },
    });
  };

  return (
    <div
      className={`group rounded-2xl bg-gray-200 cursor-pointer hover:shadow-3xl transition duration-700 ease-in-out h-full ${
        className || ''
      }`}
    >
      <Link className="w-full h-full" href={detailUrl}>
        <div className="flex">
          <Image
            className="rounded-2xl group-hover:rounded-b-none w-full aspect-[286/163] ease-in duration-300"
            width="100%"
            preview={false}
            src={result.internalChallenge?.banner?.url || ''}
            alt={result.internalChallenge?.title}
            fallback="https://dummyimage.com/600x400/F5F5F5/fff.png&text=+"
          />
        </div>
        <div className="py-4 space-y-4 h-max">
          <div className="px-2 space-y-2">
            {result.internalChallenge?.innovationSpaceTag && (
              <div className="text-sm font-light text-gray-900">
                {t('space_with_name', {
                  name: result.internalChallenge?.innovationSpaceTag?.name,
                })}
              </div>
            )}
            <div className="mt-2 flex justify-between">
              <Text className="!text-gray-900 !text-lg !font-medium line-clamp-3">
                {result.internalChallenge?.title}
              </Text>
            </div>
            <ul className="text-gray-900 space-y-[10px] mb-0 mt-2">
              {details.map((detail, index) => (
                <li
                  className="text-sm font-light flex items-center gap-x-3"
                  key={index}
                >
                  <span className="text-lg">{detail.icon}</span>
                  {detail.text}
                </li>
              ))}
            </ul>
          </div>
          {result.reasoning && (
            <div
              onClick={(e) => {
                e.preventDefault();
                setCollapsed(!collapsed);
              }}
            >
              <SuggestionCard>
                <div className="mb-2">
                  <span>{t('challenge_is')}</span>{' '}
                  <ScoreTag score={result.score} />{' '}
                  <span>{t('to_your_idea')}</span>
                </div>
                <div className="relative">
                  <span
                    className={`line-clamp-2 ${
                      collapsed ? 'line-clamp-none' : ''
                    }`}
                  >
                    {t('reason_result', { reason: result.reasoning })}
                  </span>
                  {!collapsed && (
                    <div className="absolute text-right bg-white bottom-0 right-0">
                      <span>...</span>{' '}
                      <span className="text-primary-600">{t('read_more')}</span>
                    </div>
                  )}
                </div>
              </SuggestionCard>
            </div>
          )}
          {result.internalChallenge?.__typename === 'InternalChallenge' && (
            <div className="cursor-pointer stroke-gray-900">
              <Button
                type="primary"
                shape="round"
                disabled={!checkApplyEnabled()}
                onClick={handleApply}
                loading={submitLoading}
              >
                <span className="!flex !items-center !gap-2">
                  {t('apply_now')}
                </span>
              </Button>
            </div>
          )}
        </div>
      </Link>
    </div>
  );
}

export default ChallengeCardSuggestion;
